// Auto-fill support for traffic coming from a lander that already collected
// the applicant's data on the URL query string. We parse those params once on
// funnel entry, normalize them to the field names each step reads, and persist
// them in sessionStorage so every step can pre-fill its inputs/selects while the
// user still walks through (and confirms) each step.

const STORAGE_KEY = "landerPrefill";

// Read a trimmed, non-empty value from URLSearchParams.
const readParam = (params, key) => {
  const value = params.get(key);
  if (value === null) return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

// Read the first present value among several accepted param name aliases.
const readAny = (params, keys) => {
  for (const key of keys) {
    const value = readParam(params, key);
    if (value !== undefined) return value;
  }
  return undefined;
};

// Uppercase for enum-style fields (options compare against UPPER_CASE values).
const upper = (value) => (value === undefined ? undefined : value.toUpperCase());

// Map a debt value (number or free text) to one of the debt-amount buckets.
const mapDebtAmount = (raw) => {
  if (raw === undefined) return undefined;
  const buckets = [
    "None",
    "$1,000-$4,999",
    "$5,000-$9,999",
    "$10,000-$20,000",
    "Over $20,000"
  ];
  // Exact label match wins.
  if (buckets.includes(raw)) return raw;
  const lower = raw.toLowerCase();
  if (lower === "none" || lower === "no" || lower === "0") return "None";
  const num = Number(String(raw).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(num)) return undefined;
  if (num <= 0) return "None";
  if (num < 5000) return "$1,000-$4,999";
  if (num < 10000) return "$5,000-$9,999";
  if (num <= 20000) return "$10,000-$20,000";
  return "Over $20,000";
};

// Map raw lander params -> the field names the step forms read from Redux.
// Each field accepts several name aliases (lander style + InterateLeads API
// style) so whatever the landing page sends, we capture it. Returns an object
// containing only the fields actually present on the URL.
export const parseLanderParams = (search) => {
  const params = new URLSearchParams(search);
  const data = {};
  const set = (key, value) => {
    if (value !== undefined) data[key] = value;
  };

  set("firstName", readAny(params, ["firstName", "first_name"]));
  set("lastName", readAny(params, ["lastName", "last_name"]));
  set("email", readAny(params, ["email"]));

  // Loan amount selects compare against numeric option values.
  const amount = readAny(params, ["amount", "loanAmount", "loan_amount"]);
  if (amount !== undefined && !Number.isNaN(Number(amount))) {
    set("amount", Number(amount));
  }

  // Loan reason enum (AUTO_PURCHASE, DEBT_CONSOLIDATION, ...).
  set("loanReason", upper(readAny(params, ["loanReason", "loan_reason", "reason"])));

  // Date of birth (expected as YYYY-MM-DD). Also split into parts for the
  // hero form's month/day/year selects.
  const dob = readAny(params, ["dob", "dateOfBirth", "date_of_birth"]);
  if (dob) {
    set("dob", dob);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
    if (match) {
      set("dobYear", match[1]);
      set("dobMonth", match[2]);
      set("dobDay", match[3]);
    }
  }

  set("cellPhone", readAny(params, ["cellPhone", "cell_phone", "phone"]));
  set("zipCode", readAny(params, ["zipCode", "zipcode", "zip"]));
  set("streetAddress", readAny(params, ["streetAddress", "street_address", "address"]));
  // Income type enum (EMPLOYMENT, SELF_EMPLOYMENT, MILITARY, BENEFITS).
  set("incomeSource", upper(readAny(params, ["incomeSource", "incomeType", "income_type", "income_source"])));
  set("employerName", readAny(params, ["employerName", "workCompanyName", "employer", "company"]));
  set("jobTitle", readAny(params, ["jobTitle", "job_title"]));
  // emptime select option values are strings ("48", "24"...).
  set("currentCompanyDuration", readAny(params, ["timeEmployed", "workTimeAtEmployer", "currentCompanyDuration"]));
  // grossincome select option values are strings ("4500"...).
  set("monthlyIncome", readAny(params, ["monthlyIncome", "incomeNetMonthly", "income_monthly"]));
  // pay frequency enum (WEEKLY, BIWEEKLY, TWICEMONTHLY, MONTHLY).
  set("incomePaymentFrequency", upper(readAny(params, ["payFrequency", "incomePaymentFrequency", "pay_frequency"])));
  set("routingNumber", readAny(params, ["abaNumber", "routingNumber", "bankAba", "aba"]));
  // Credit score enum (EXCELLENT, GOOD, FAIR, POOR).
  set("creditScore", upper(readAny(params, ["creditScore", "credit_score"])));
  // Unsecured debt bucket.
  set("debtAmount", mapDebtAmount(readAny(params, ["debt_amount", "debtAmount", "debt", "unsecuredDebtAmount"])));

  return data;
};

// Parse the current URL and persist any lander data. Safe to call repeatedly;
// it only overwrites the store when the URL actually carries lander params.
export const saveLanderPrefillFromSearch = (search) => {
  try {
    const data = parseLanderParams(search);
    if (Object.keys(data).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    return data;
  } catch (_) {
    return {};
  }
};

// Read the persisted lander data (empty object if none).
export const getLanderPrefill = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (_) {
    return {};
  }
};

// Convenience accessor for a single pre-filled field.
export const prefillValue = (field) => getLanderPrefill()[field];
